import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  addDoc,
  Timestamp,
} from "firebase/firestore";
import { firebaseApp } from "../providers/firebase";
import {
  collectionNames,
} from "./dictionary/collectionNames";
import {
  getRangeFromSelection,
  isWithinRange,
} from "../utils/insighthubDateRange";

export const db = getFirestore(firebaseApp);

export async function updateLoginActivity(
  userId: string,
  email: string,
  type: string,
  role: string
) {
  try {
    const today = new Date().toISOString().slice(0, 10);

    const q = query(
      collection(db, collectionNames.loginActivity),
      where("userId", "==", userId)
    );

    const snapshot = await getDocs(q);

    let todayDocRef: any = null;

    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (!data?.createdAt) return;

      const createdDate = data.createdAt
        .toDate()
        .toISOString()
        .slice(0, 10);

      if (createdDate === today) {
        todayDocRef = docSnap.ref;
      }
    });

    const platformField = "platform.Web_app";

    if (todayDocRef) {
      // Same-day login → update lastLogin + Web platform timestamp
      await updateDoc(todayDocRef, {
        lastLogin: Timestamp.now(),
        [platformField]: Timestamp.now(),
      });
    } else {
      // First login today → create new document
      await addDoc(collection(db, collectionNames.loginActivity), {
        userId,
        email,
        role,
        type,
        platform: {
          Web_app: Timestamp.now(),
        },
        createdAt: Timestamp.now(),
        lastLogin: Timestamp.now(),
      });
    }
  } catch (err) {
    console.error("Error updating Web login activity:", err);
  }
};

export async function fetchLoginActivity(days: number = 30) {
  try {
    const now = new Date();

    const fromDate = new Date();
    fromDate.setDate(now.getDate() - days);

    const q = query(
      collection(db, "LoginActivity"),
      where("createdAt", ">=", Timestamp.fromDate(fromDate))
    );

    const snapshot = await getDocs(q);

    const data = snapshot.docs.map((doc) => {
      const d = doc.data();
      const createdAtDate = d?.createdAt?.toDate?.();

      return {
        docId: doc.id,
        userId: d?.userId || null,
        createdAt: createdAtDate
          ? createdAtDate.toISOString().split("T")[0] // YYYY-MM-DD
          : null,
      };
    });

    return data;
  } catch (error) {
    console.error("Error fetching LoginActivity:", error);
    return [];
  }
}

export async function fetchMonthlyActiveLoginCount(
  params: { days?: number } = {}
) {
  try {
    const days = params.days ?? 30;

    // Calculate start date
    const now = new Date();
    const fromDate = new Date();
    fromDate.setDate(now.getDate() - days);

    const q = query(
      collection(db, "LoginActivity"),
      where("createdAt", ">=", Timestamp.fromDate(fromDate))
    );

    const snapshot = await getDocs(q);

    // Track unique users
    const uniqueUserIds = new Set<string>();

    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      if (data.userId) {
        uniqueUserIds.add(data.userId);
      }
    });

    return {
      count: uniqueUserIds.size
    };
  } catch (error) {
    console.error("Error fetching monthly active login count:", error);
    return {
      count: 0
    };
  }
}

export async function fetchMonthlyActiveUsers(
  params: { days?: number } = {}
) {
  try {
    const days = params.days ?? 30;

    const now = new Date();
    const fromDate = new Date();
    fromDate.setDate(now.getDate() - days);

    const q = query(
      collection(db, "LoginActivity"),
      where("createdAt", ">=", Timestamp.fromDate(fromDate))
    );

    const snapshot = await getDocs(q);

    const userMap = new Map<
      string,
      {
        email: string;
        role: string;
        type: string;
        lastActivity: Date;
      }
    >();

    snapshot.docs.forEach((doc) => {
      const d = doc.data();
      if (!d.userId || !d.lastLogin) return;

      const lastLoginDate = d.lastLogin.toDate();
      const existing = userMap.get(d.userId);

      if (!existing || lastLoginDate > existing.lastActivity) {
        userMap.set(d.userId, {
          email: d.email ?? "",
          role: d.role ?? "",
          type: d.type ?? "",
          lastActivity: lastLoginDate
        });
      }
    });

    const users = Array.from(userMap.values()).map((u) => ({
      email: u.email,
      role: u.role,
      type: u.type,
      lastActivity: u.lastActivity.toISOString()
    }));

    // 👇 Match callable cloud function response shape
    return {
      data: {
        data: users
      }
    };
  } catch (error) {
    console.error("Error fetching monthly active users:", error);
    return {
      data: {
        data: []
      }
    };
  }
}

function rangeToFirestoreBounds(startDate: Date, endDate: Date) {
  const rangeStart = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate(),
    0,
    0,
    0,
    0
  );
  const rangeEnd = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate(),
    23,
    59,
    59,
    999
  );
  return { rangeStart, rangeEnd };
}

/**
 * Active seeker counts by platform for the same calendar / rolling ranges as
 * Seeker Activity Trends (including previous calendar quarter for 90 and
 * previous calendar year for 365).
 */
export async function fetchPlatformWiseActiveUsers(selection: number = 7) {
  try {
    const { startDate, endDate } = getRangeFromSelection(selection);
    const { rangeStart, rangeEnd } = rangeToFirestoreBounds(
      startDate,
      endDate
    );

    const q = query(
      collection(db, "LoginActivity"),
      where("createdAt", ">=", Timestamp.fromDate(rangeStart)),
      where("createdAt", "<=", Timestamp.fromDate(rangeEnd))
    );

    const snapshot = await getDocs(q);

    const userPlatformMap = new Map<
      string,
      {
        web?: Date;
        android?: Date;
        ios?: Date;
      }
    >();

    snapshot.forEach((doc) => {
      const data = doc.data();
      const userId = data.userId;
      if (!userId || !data.platform) return;

      const existing = userPlatformMap.get(userId) || {};
      const platform = data.platform;

      if (platform.Web_app) {
        const t = platform.Web_app.toDate();
        if (!existing.web || t > existing.web) existing.web = t;
      }

      if (platform.Android_app) {
        const t = platform.Android_app.toDate();
        if (!existing.android || t > existing.android)
          existing.android = t;
      }

      if (platform.IOS_app) {
        const t = platform.IOS_app.toDate();
        if (!existing.ios || t > existing.ios) existing.ios = t;
      }

      userPlatformMap.set(userId, existing);
    });

    let ios = 0;
    let android = 0;
    let web = 0;

    userPlatformMap.forEach((platforms) => {
      if (
        platforms.web &&
        isWithinRange(platforms.web, startDate, endDate)
      ) {
        web++;
      }
      if (
        platforms.android &&
        isWithinRange(platforms.android, startDate, endDate)
      ) {
        android++;
      }
      if (
        platforms.ios &&
        isWithinRange(platforms.ios, startDate, endDate)
      ) {
        ios++;
      }
    });

    return {
      data: {
        ios,
        android,
        web,
      },
    };
  } catch (error) {
    console.error("Error fetching platform-wise active users:", error);
    return {
      data: {
        ios: 0,
        android: 0,
        web: 0,
      },
    };
  }
}

export async function fetchActiveUsersPlatformWiseCSV(
  params?: { days?: number; selection?: number }
) {
  try {
    const selection = Number(
      params?.selection ?? params?.days ?? 30
    );
    type PlatformKey = "Web_app" | "Android_app" | "IOS_app";

    if (Number.isNaN(selection) || selection <= 0) {
      throw new Error("Invalid selection parameter");
    }

    const { startDate, endDate } = getRangeFromSelection(selection);
    const { rangeStart, rangeEnd } = rangeToFirestoreBounds(
      startDate,
      endDate
    );

    const q = query(
      collection(db, "LoginActivity"),
      where("createdAt", ">=", Timestamp.fromDate(rangeStart)),
      where("createdAt", "<=", Timestamp.fromDate(rangeEnd))
    );

    const snapshot = await getDocs(q);

    const userPlatformMap = new Map<
      string,
      {
        email?: string;
        role?: string;
        type?: string;
        platforms: Partial<Record<PlatformKey, Date>>;
      }
    >();

    snapshot.forEach((doc) => {
      const data = doc.data();
      const userId = data.userId;
      if (!userId || !data.platform) return;

      const existing: any =
        userPlatformMap.get(userId) || {
          email: data.email,
          role: data.role,
          type: data.type,
          platforms: {},
        };

      (["Web_app", "Android_app", "IOS_app"] as PlatformKey[]).forEach(
        (platformKey) => {
          const ts = data.platform?.[platformKey];
          if (ts?.toDate) {
            const loginDate = ts.toDate();
            const prev = existing.platforms[platformKey];
            if (!prev || loginDate > prev) {
              existing.platforms[platformKey] = loginDate;
            }
          }
        }
      );

      userPlatformMap.set(userId, existing);
    });

    const result: any[] = [];

    userPlatformMap.forEach((user) => {
      Object.entries(user.platforms).forEach(
        ([platform, lastLogin]) => {
          const d = lastLogin as Date;
          if (!isWithinRange(d, startDate, endDate)) return;
          result.push({
            email: user.email || "",
            lastActivity: formatDate(d),
            role: user.role || "",
            type: user.type || "",
            platform,
          });
        }
      );
    });

    return { data: { data: result } };
  } catch (error) {
    console.error("Error fetching active users CSV:", error);
    return { data: { data: [] } };
  }
}

function formatDate(date: Date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return "";
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}